/**
 * The draft everybody is editing, loaded once and shared.
 *
 * Every Customer Bot screen renders some part of one document, and every save replaces a row in
 * the same draft. Loading it per screen would mean five copies that disagree the moment someone
 * saves — and the one thing this dashboard must never do is show an editor a stale version of the
 * config they just changed.
 *
 * ## Saving is optimistic about the network, not about the server
 *
 * A save posts and then reloads the whole config. Reloading is a round trip nobody enjoys, but the
 * server is the thing that decides — it may have created the draft, renumbered a version, or
 * refused — and a client that patched its own state would be guessing at all three. The screens
 * are small and the config is a few kilobytes.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  fetchBotConfig,
  fetchCurrentVersions,
  useToast,
  type BotConfig,
  type BotVersion,
} from "@nab/shared";

export interface BotStore {
  config: BotConfig | null;
  published: BotVersion | null;
  draft: BotVersion | null;
  loading: boolean;
  /** Set when the config could not be loaded at all — usually "nothing has been seeded yet". */
  problem: string | null;
  reload: () => Promise<void>;
  /** Wraps a save: runs it, reloads, and reports a refusal in the server's own words. */
  save: (what: () => Promise<unknown>, success?: string) => Promise<boolean>;
}

const BotContext = createContext<BotStore | null>(null);

export function BotProvider({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation("app");
  const toast = useToast();
  const [config, setConfig] = useState<BotConfig | null>(null);
  const [published, setPublished] = useState<BotVersion | null>(null);
  const [draft, setDraft] = useState<BotVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [loaded, current] = await Promise.all([
        fetchBotConfig(),
        fetchCurrentVersions(),
      ]);
      setConfig(loaded);
      setPublished(current.published);
      setDraft(current.draft);
      setProblem(null);
    } catch (caught) {
      // A 404 here is the ordinary "nobody has seeded this database" case and reads as an
      // instruction rather than an error; anything else is shown as the server worded it.
      setProblem(
        caught instanceof ApiError && typeof caught.detail === "string"
          ? caught.detail
          : t("loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (what: () => Promise<unknown>, success?: string) => {
      try {
        await what();
        await reload();
        if (success) toast.show({ tone: "success", message: success });
        return true;
      } catch (caught) {
        toast.show({
          tone: "error",
          message: t("saveFailed"),
          ...(caught instanceof ApiError && typeof caught.detail === "string"
            ? { detail: caught.detail }
            : {}),
        });
        return false;
      }
    },
    [reload, toast, t],
  );

  const value = useMemo(
    () => ({ config, published, draft, loading, problem, reload, save }),
    [config, published, draft, loading, problem, reload, save],
  );

  return <BotContext.Provider value={value}>{children}</BotContext.Provider>;
}

export function useBot(): BotStore {
  const store = useContext(BotContext);
  if (!store) throw new Error("useBot must be used inside a BotProvider");
  return store;
}

/**
 * Every language-script the config can be answered in, derived from `settings`.
 *
 * Derived rather than hardcoded, because adding a language is a dashboard change: a hardcoded list
 * would leave every editor grid showing the old languages after someone added a new one.
 */
export function tagsOf(config: BotConfig | null): string[] {
  const settings = config?.settings ?? {};
  const languages = settings.languages ?? ["en"];
  const scripts = settings.scripts ?? {};
  const tags: string[] = [];
  for (const language of languages) {
    const variants = scripts[language];
    if (variants && variants.length > 0) {
      for (const script of variants) tags.push(`${language}-${script}`);
    } else {
      tags.push(language);
    }
  }
  return tags;
}
